/**
 * useTokenPrice Hook
 * Provides real-time token price data for React components
 */

import { useState, useEffect, useCallback } from 'react'
import { getTokenPriceUsd, getTokenValueUsd, formatUsdValue } from '@/services/tokenPriceService'
import { isPageVisible } from '@/lib/pageVisibility'

interface UseTokenPriceResult {
  price: number | null
  loading: boolean
  error: string | null
  refetch: () => void
}

interface UseTokenValueResult {
  value: number | null
  formatted: string
  loading: boolean
  error: string | null
}

/**
 * Get the current USD price of a token
 */
export function useTokenPrice(
  symbol: string,
  chainId: number,
  refreshInterval: number = 30000
): UseTokenPriceResult {
  const [price, setPrice] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPrice = useCallback(async () => {
    if (!isPageVisible()) return
    if (!symbol || !chainId) {
      setPrice(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const result = await getTokenPriceUsd(symbol, chainId)
      setPrice(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch price')
      setPrice(null)
    } finally {
      setLoading(false)
    }
  }, [symbol, chainId])

  useEffect(() => {
    fetchPrice()

    if (refreshInterval > 0) {
      const interval = setInterval(fetchPrice, refreshInterval)
      return () => clearInterval(interval)
    }
  }, [fetchPrice, refreshInterval])

  return { price, loading, error, refetch: fetchPrice }
}

/**
 * Calculate the USD value of a token amount
 */
export function useTokenValue(
  symbol: string,
  chainId: number,
  amount: bigint | null,
  decimals: number = 9
): UseTokenValueResult {
  const [value, setValue] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const calculateValue = async () => {
      if (!symbol || !chainId || amount === null || amount === 0n) {
        setValue(null)
        return
      }

      try {
        setLoading(true)
        setError(null)
        const result = await getTokenValueUsd(symbol, chainId, amount, decimals)
        setValue(result)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to calculate value')
        setValue(null)
      } finally {
        setLoading(false)
      }
    }

    // Debounce to avoid excessive calls
    const timer = setTimeout(calculateValue, 300)
    return () => clearTimeout(timer)
  }, [symbol, chainId, amount, decimals])

  return {
    value,
    formatted: formatUsdValue(value),
    loading,
    error,
  }
}

/**
 * Get formatted USD value for a token amount (simplified hook)
 */
export function useFormattedTokenValue(
  symbol: string,
  chainId: number,
  amount: bigint | null,
  decimals: number = 9
): string {
  const { formatted } = useTokenValue(symbol, chainId, amount, decimals)
  return formatted
}
